import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ptBR } from 'date-fns/locale/pt-BR';
import Select from "react-select";
import { toast } from "react-toastify";
import { apiService } from '../../../services/api';
import { useUser } from '../../../context/userContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { ProtectedValue } from '../../../components/ProtectedValue';
import {
  Empenho,
  EtapaEmpenho,
  EmpenhoFiltrosGeral,
} from '../../../types/empenhos';
import {
  getEtapaLabel as getEtapaLabelUtil,
  getEtapaBadge as getEtapaBadgeUtil,
  isNovoFluxo
} from './utils/etapaUtils';
import EmpenhoDetalhes from './components/EmpenhoDetalhes';
import './empenhos.css';
// import ScrollableTable from '../contracts/components/ScrollableTable'; // Não usado no momento

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

const MONTHS_SHORT = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};

registerLocale('pt-BR', customPtBR as any);

// Opções de etapas para o filtro
const etapaOptions = [
  { value: 'COMPRAS', label: 'Compras' },
  { value: 'PAGAMENTO', label: 'Pagamento' },
  { value: 'NF_ENTRADA', label: 'NF de Entrada' },
  { value: 'NF_SAIDA', label: 'NF de Saída' },
  { value: 'ENTREGA', label: 'Entrega' },
  { value: 'CONCLUIDO', label: 'Concluído' },
  { value: 'CANCELADO', label: 'Cancelado' }
];

// Funções de utilitário importadas
const getEtapaLabel = (etapa: EtapaEmpenho): string => {
  return getEtapaLabelUtil(etapa);
};

const getEtapaBadgeClass = (etapa: EtapaEmpenho): string => {
  return `badge ${getEtapaBadgeUtil(etapa)}`;
};

// Interface para os cards de resumo
interface CardResumo {
  total_empenhos: number;
  total_valor: number;
  em_andamento: number;
  em_andamento_valor: number;
  em_andamento_detalhes: {
    COMPRAS: { count: number; valor: number };
    PAGAMENTO: { count: number; valor: number };
    NF_ENTRADA: { count: number; valor: number };
    NF_SAIDA: { count: number; valor: number };
    ENTREGA: { count: number; valor: number };
  };
  concluidos: number;
  concluidos_valor: number;
  cancelados: number;
  cancelados_valor: number;
}

const Empenhos = () => {
  const { userData } = useUser();
  const { hasPermission } = usePermissions();
  const podeAprovarPendente = hasPermission('AUTORIZACOES', 'APROVAR_PENDENTE');

  // Estados principais
  const [empenhos, setEmpenhos] = useState<Empenho[]>([]);
  const [filteredEmpenhos, setFilteredEmpenhos] = useState<Empenho[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Abas
  const [activeTab, setActiveTab] = useState<'todas' | 'minhas' | 'pendentes'>('todas');
  const [minhasAutorizacoes, setMinhasAutorizacoes] = useState<Empenho[]>([]);
  const [isLoadingMinhas, setIsLoadingMinhas] = useState(false);
  const [minhasJaBuscadas, setMinhasJaBuscadas] = useState(false);
  const [minhasCountInicial, setMinhasCountInicial] = useState<number | null>(null);

  // Aba "Pendentes de Aprovação"
  const [pendentesAutorizacoes, setPendentesAutorizacoes] = useState<Empenho[]>([]);
  const [isLoadingPendentes, setIsLoadingPendentes] = useState(false);
  const [pendentesJaBuscados, setPendentesJaBuscados] = useState(false);
  const [pendentesCountInicial, setPendentesCountInicial] = useState<number | null>(null);

  // Estados para resumo (cards)
  const [resumo, setResumo] = useState<CardResumo>({
    total_empenhos: 0,
    total_valor: 0,
    em_andamento: 0,
    em_andamento_valor: 0,
    em_andamento_detalhes: {
      COMPRAS: { count: 0, valor: 0 },
      PAGAMENTO: { count: 0, valor: 0 },
      NF_ENTRADA: { count: 0, valor: 0 },
      NF_SAIDA: { count: 0, valor: 0 },
      ENTREGA: { count: 0, valor: 0 },
    },
    concluidos: 0,
    concluidos_valor: 0,
    cancelados: 0,
    cancelados_valor: 0
  });

  // Estados para filtros
  const [dataInicio, setDataInicio] = useState<Date | null>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [dataFim, setDataFim] = useState<Date | null>(new Date());
  const [etapasSelecionadas, setEtapasSelecionadas] = useState<any[]>([]);
  const [contratoSelecionado, setContratoSelecionado] = useState<any>(null);
  const [codigoBusca, setCodigoBusca] = useState<string>('');
  const [idBusca, setIdBusca] = useState<string>('');

  // Estados para opções dos selects
  const [contratos, setContratos] = useState<{ value: number, label: string }[]>([]);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Estados para detalhes do empenho
  const [empenhoSelecionadoId, setEmpenhoSelecionadoId] = useState<number | null>(null);
  const [isDetalhesOpen, setIsDetalhesOpen] = useState(false);
  const [initialModalTab, setInitialModalTab] = useState<'dados' | 'itens' | 'solicitacao' | 'historico' | 'entregas' | 'anexos'>('dados');

  // Carregar dados iniciais
  useEffect(() => {
    carregarContratos();
    carregarEmpenhos();
    apiService.getMinhasAutorizacoesCount().then(setMinhasCountInicial).catch(() => {});
    if (podeAprovarPendente) {
      apiService.getPendentesAprovacaoCount().then(setPendentesCountInicial).catch(() => {});
    }
  }, []);

  // Carregar contratos para o select
  const carregarContratos = async () => {
    try {
      const response = await apiService.getContratos({});
      if (response.success && response.data) {
        const options = response.data.map(contrato => ({
          value: contrato.id,
          label: `Contrato #${contrato.id} - ${contrato.oportunidade_titulo || contrato.descricao || 'Sem descrição'}`
        }));
        setContratos(options);
      }
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    }
  };

  // Carregar empenhos com filtros
  const carregarEmpenhos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const filtros: EmpenhoFiltrosGeral = {};

      // Aplicar filtros
      if (etapasSelecionadas && etapasSelecionadas.length > 0) {
        filtros.etapa = etapasSelecionadas.map(e => e.value as EtapaEmpenho);
      }

      if (contratoSelecionado) {
        filtros.id_contrato = contratoSelecionado.value;
      }

      if (dataInicio) {
        filtros.data_de = dataInicio.toISOString().split('T')[0];
      }

      if (dataFim) {
        filtros.data_ate = dataFim.toISOString().split('T')[0];
      }

      if (codigoBusca.trim()) {
        filtros.codigo_empenho = codigoBusca.trim();
      }

      if (idBusca.trim()) {
        filtros.id = idBusca.trim();
      }

      const response = await apiService.getEmpenhosGeral(filtros);

      console.log('Resposta da API:', response);

      if (response.success) {
        // A API retorna os dados em response.data.data (aninhado)
        let empenhosData: Empenho[] = [];

        if (response.data && typeof response.data === 'object') {
          // Se response.data é um objeto com propriedade data
          if ('data' in response.data && Array.isArray(response.data.data)) {
            empenhosData = response.data.data;
          }
          // Se response.data é diretamente um array
          else if (Array.isArray(response.data)) {
            empenhosData = response.data;
          }
        }

        console.log('Empenhos carregados:', empenhosData);

        setEmpenhos(empenhosData);
        setFilteredEmpenhos(empenhosData);
        calcularResumo(empenhosData);
      } else {
        throw new Error(response.message || 'Erro ao carregar empenhos');
      }
    } catch (error: any) {
      console.error('Erro ao carregar empenhos:', error);
      setError(error.message || 'Erro ao carregar empenhos');
      toast.error(error.message || 'Erro ao carregar empenhos');
      setEmpenhos([]);
      setFilteredEmpenhos([]);
      calcularResumo([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Calcular resumo dos cards
  const calcularResumo = (data: Empenho[]) => {
    // Garantir que data é um array
    if (!Array.isArray(data)) {
      console.warn('calcularResumo recebeu dados inválidos:', data);
      data = [];
    }

    const total_empenhos = data.length;
    const total_valor = data.reduce((sum, e) => sum + (Number(e.valor_total) || 0), 0);

    const em_andamento_list = data.filter(e =>
      !['ENTREGA', 'CONCLUIDO', 'CANCELADO'].includes(e.etapa)
    );
    const em_andamento = em_andamento_list.length;
    const em_andamento_valor = em_andamento_list.reduce((sum, e) => sum + (Number(e.valor_total) || 0), 0);

    const em_andamento_detalhes = {
      COMPRAS: { count: 0, valor: 0 },
      PAGAMENTO: { count: 0, valor: 0 },
      NF_ENTRADA: { count: 0, valor: 0 },
      NF_SAIDA: { count: 0, valor: 0 },
      ENTREGA: { count: 0, valor: 0 },
    };

    em_andamento_list.forEach(e => {
      const etapa = e.etapa as keyof typeof em_andamento_detalhes;
      if (em_andamento_detalhes[etapa]) {
        em_andamento_detalhes[etapa].count += 1;
        em_andamento_detalhes[etapa].valor += (Number(e.valor_total) || 0);
      }
    });

    const concluidos_list = data.filter(e => e.etapa === 'CONCLUIDO');
    const concluidos = concluidos_list.length;
    const concluidos_valor = concluidos_list.reduce((sum, e) => sum + (Number(e.valor_total) || 0), 0);

    const cancelados_list = data.filter(e => e.etapa === 'CANCELADO');
    const cancelados = cancelados_list.length;
    const cancelados_valor = cancelados_list.reduce((sum, e) => sum + (Number(e.valor_total) || 0), 0);

    setResumo({
      total_empenhos,
      total_valor,
      em_andamento,
      em_andamento_valor,
      em_andamento_detalhes,
      concluidos,
      concluidos_valor,
      cancelados,
      cancelados_valor
    });
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    carregarEmpenhos();
  };

  // Limpar filtros
  const limparFiltros = () => {
    setDataInicio(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    setDataFim(new Date());
    setEtapasSelecionadas([]);
    setContratoSelecionado(null);
    setCodigoBusca('');
    setIdBusca('');
    // Recarregar sem filtros
    setTimeout(() => carregarEmpenhos(), 100);
  };

  // Formatar valor em reais
  const formatarValor = (valor: number): string => {
    const valorNumerico = Number(valor) || 0;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valorNumerico);
  };

  // Formatar data
  const formatarData = (data: string): string => {
    if (!data) return '-';
    try {
      // Pega apenas a parte da data "YYYY-MM-DD" e reconstrói para evitar subtração de fuso horário
      const datePart = data.split('T')[0].split(' ')[0];
      const [year, month, day] = datePart.split('-');
      if (year && month && day) {
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      // Fallback
    }
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  // Busca lazy das "minhas autorizações" ao clicar na aba — exibe apenas COMPRAS
  const handleTabMinhas = async () => {
    setActiveTab('minhas');
    if (minhasJaBuscadas) return;
    setIsLoadingMinhas(true);
    try {
      const response = await apiService.getMinhasAutorizacoes();
      const data = Array.isArray(response.data)
        ? response.data.filter(e => e.etapa === 'COMPRAS')
        : [];
      setMinhasAutorizacoes(data);
      setMinhasJaBuscadas(true);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao buscar minhas autorizações');
    } finally {
      setIsLoadingMinhas(false);
    }
  };

  // Busca lazy dos "pendentes de aprovação" — todos da conta em etapa PENDENTE
  const handleTabPendentes = async () => {
    setActiveTab('pendentes');
    if (pendentesJaBuscados) return;
    setIsLoadingPendentes(true);
    try {
      const response = await apiService.getEmpenhosGeral({ etapa: ['PENDENTE'] as any });
      const raw: any = response.data;
      const data: Empenho[] = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
      setPendentesAutorizacoes(data);
      setPendentesJaBuscados(true);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao buscar pendentes de aprovação');
    } finally {
      setIsLoadingPendentes(false);
    }
  };

  // Badge da aba "Minhas": antes de carregar usa a contagem leve do mount; depois usa o tamanho da lista (já filtrada por COMPRAS)
  const minhasComprasCount = minhasJaBuscadas
    ? minhasAutorizacoes.length
    : (minhasCountInicial ?? 0);

  // Badge da aba "Pendentes": mesmo padrão
  const pendentesCount = pendentesJaBuscados
    ? pendentesAutorizacoes.length
    : (pendentesCountInicial ?? 0);

  // Ordenação por data
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSortData = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
    setCurrentPage(1);
  };

  // Paginação — adapta ao tab ativo e aplica ordenação
  const activeList = (
    activeTab === 'minhas' ? minhasAutorizacoes :
    activeTab === 'pendentes' ? pendentesAutorizacoes :
    filteredEmpenhos
  ).slice()
    .sort((a, b) => {
      const da = a.data_empenho ?? '';
      const db = b.data_empenho ?? '';
      return sortDirection === 'asc' ? da.localeCompare(db) : db.localeCompare(da);
    });
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeList.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activeList.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Abrir detalhes do empenho
  const abrirDetalhes = (id: number, tab?: 'dados' | 'itens' | 'solicitacao' | 'historico' | 'entregas' | 'anexos') => {
    setEmpenhoSelecionadoId(id);
    setInitialModalTab(tab || 'dados');
    setIsDetalhesOpen(true);
  };

  // Fechar detalhes
  const fecharDetalhes = () => {
    setIsDetalhesOpen(false);
    setEmpenhoSelecionadoId(null);
  };

  // Atualizar após mudanças
  const handleUpdateEmpenho = async () => {
    carregarEmpenhos();
    fecharDetalhes();

    // Atualiza contagens dos badges (aba + menu lateral)
    try {
      const [countMinhas, countPendentes] = await Promise.all([
        apiService.getMinhasAutorizacoesCount(),
        apiService.getPendentesAprovacaoCount(),
      ]);
      setMinhasCountInicial(countMinhas);
      setPendentesCountInicial(countPendentes);
    } catch {}

    // Re-busca "Minhas" se já foi carregada
    if (minhasJaBuscadas) {
      try {
        const response = await apiService.getMinhasAutorizacoes();
        const data = Array.isArray(response.data)
          ? response.data.filter(e => e.etapa === 'COMPRAS')
          : [];
        setMinhasAutorizacoes(data);
      } catch {}
    }

    // Re-busca "Pendentes" se já foi carregada
    if (pendentesJaBuscados) {
      try {
        const response = await apiService.getEmpenhosGeral({ etapa: ['PENDENTE'] as any });
        const raw: any = response.data;
        const data: Empenho[] = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
        setPendentesAutorizacoes(data);
      } catch {}
    }

    // Sinaliza o sidebar para atualizar os badges
    window.dispatchEvent(new CustomEvent('empenho-status-changed'));
  };

  // Visualizar anexo por tipo
  const visualizarAnexo = async (idEmpenho: number, tipoDocumento: string) => {
    try {
      const response = await apiService.getAnexosEmpenho({
        id_empenho: idEmpenho,
        tipo_documento: tipoDocumento,
        page: 1,
        per_page: 1
      });

      if (response.success && response.data && response.data.length > 0) {
        const anexo = response.data[0];
        const arquivos = JSON.parse(anexo.arquivos || '[]');

        if (arquivos.length > 0) {
          const arquivo = arquivos[0];
          // Download do arquivo
          await apiService.downloadArquivoAnexoEmpenho(anexo.id, arquivo.nome_salvo);
        } else {
          toast.warning(`Nenhum arquivo encontrado para ${tipoDocumento}`);
        }
      } else {
        toast.info(`Nenhum anexo de ${tipoDocumento} encontrado`);
      }
    } catch (error) {
      console.error('Erro ao visualizar anexo:', error);
      toast.error('Erro ao buscar anexo');
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <h3 className="page-title">Autorizações</h3>
                </div>
                <div className="col-md-8 text-end">

                </div>
              </div>
            </div>

            {/* Cards de Resumo */}
            <div className="row mb-4">
              <div className="col-xl-3 col-sm-6 col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-primary">
                        <i className="ti ti-file-text" />
                      </span>
                      <div className="dash-count">
                        <h5 className="mb-1">{resumo.total_empenhos}</h5>
                        <p className="mb-0">Total de Autorizações</p>
                      </div>
                    </div>
                    <div className="dash-widget-footer">
                      <span className="text-muted"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.total_valor)} /></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-sm-6 col-12">
                <style>
                  {`
                    .card-em-andamento {
                      cursor: pointer;
                      position: relative;
                      z-index: 10;
                    }
                    .card-em-andamento .detalhes-em-andamento {
                      max-height: 0;
                      opacity: 0;
                      overflow: hidden;
                      transition: all 0.4s ease-in-out;
                      margin-top: 0;
                    }
                    .card-em-andamento:hover .detalhes-em-andamento {
                      max-height: 300px;
                      opacity: 1;
                      margin-top: 15px;
                      border-top: 1px solid rgba(0,0,0,0.1);
                      padding-top: 15px;
                    }
                    .detalhe-item {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 8px;
                      font-size: 0.85rem;
                    }
                    .detalhe-item:last-child {
                      margin-bottom: 0;
                    }
                    .detalhe-label {
                      font-weight: 500;
                      color: #495057;
                    }
                    .detalhe-valor {
                      color: #6c757d;
                      font-weight: 500;
                    }
                  `}
                </style>
                <div className="card card-em-andamento shadow-sm">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-warning text-white">
                        <i className="ti ti-clock" />
                      </span>
                      <div className="dash-count">
                        <h5 className="mb-1">{resumo.em_andamento}</h5>
                        <p className="mb-0">Em Andamento</p>
                      </div>
                    </div>
                    <div className="dash-widget-footer">
                      <span className="text-muted fw-bold"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_valor)} /></span>
                    </div>

                    <div className="detalhes-em-andamento">
                      <div className="detalhe-item">
                        <span className="detalhe-label"><span style={{ color: '#0d6efd', marginRight: '5px' }}>●</span> Compras <small className="text-muted">({resumo.em_andamento_detalhes.COMPRAS.count})</small></span>
                        <span className="detalhe-valor"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_detalhes.COMPRAS.valor)} /></span>
                      </div>
                      <div className="detalhe-item">
                        <span className="detalhe-label"><span style={{ color: '#17a2b8', marginRight: '5px' }}>●</span> Pagamento <small className="text-muted">({resumo.em_andamento_detalhes.PAGAMENTO.count})</small></span>
                        <span className="detalhe-valor"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_detalhes.PAGAMENTO.valor)} /></span>
                      </div>
                      <div className="detalhe-item">
                        <span className="detalhe-label"><span style={{ color: '#0d9488', marginRight: '5px' }}>●</span> NF Entrada <small className="text-muted">({resumo.em_andamento_detalhes.NF_ENTRADA.count})</small></span>
                        <span className="detalhe-valor"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_detalhes.NF_ENTRADA.valor)} /></span>
                      </div>
                      <div className="detalhe-item">
                        <span className="detalhe-label"><span style={{ color: '#fd7e14', marginRight: '5px' }}>●</span> NF de Saída <small className="text-muted">({resumo.em_andamento_detalhes.NF_SAIDA.count})</small></span>
                        <span className="detalhe-valor"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_detalhes.NF_SAIDA.valor)} /></span>
                      </div>
                      <div className="detalhe-item">
                        <span className="detalhe-label"><span style={{ color: '#28a745', marginRight: '5px' }}>●</span> Entrega <small className="text-muted">({resumo.em_andamento_detalhes.ENTREGA.count})</small></span>
                        <span className="detalhe-valor"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.em_andamento_detalhes.ENTREGA.valor)} /></span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-sm-6 col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-success">
                        <i className="ti ti-check" />
                      </span>
                      <div className="dash-count">
                        <h5 className="mb-1">{resumo.concluidos}</h5>
                        <p className="mb-0">Concluídos</p>
                      </div>
                    </div>
                    <div className="dash-widget-footer">
                      <span className="text-muted"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.concluidos_valor)} /></span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-xl-3 col-sm-6 col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="dash-widget-header">
                      <span className="dash-widget-icon bg-danger">
                        <i className="ti ti-x" />
                      </span>
                      <div className="dash-count">
                        <h5 className="mb-1">{resumo.cancelados}</h5>
                        <p className="mb-0">Cancelados</p>
                      </div>
                    </div>
                    <div className="dash-widget-footer">
                      <span className="text-muted"><ProtectedValue module="AUTORIZACOES" value={formatarValor(resumo.cancelados_valor)} /></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filtros */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">Data Início</label>
                    <DatePicker
                      selected={dataInicio}
                      onChange={(date) => setDataInicio(date)}
                      className="form-control datetimepicker"
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      placeholderText="Selecione"
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">Data Fim</label>
                    <DatePicker
                      selected={dataFim}
                      onChange={(date) => setDataFim(date)}
                      className="form-control datetimepicker"
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      placeholderText="Selecione"
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">Etapa</label>
                    <Select
                      isMulti
                      options={etapaOptions}
                      value={etapasSelecionadas}
                      onChange={(selected) => setEtapasSelecionadas(selected ? Array.from(selected) : [])}
                      placeholder="Todas"
                      classNamePrefix="react-select"
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">Contrato</label>
                    <Select
                      options={contratos}
                      value={contratoSelecionado}
                      onChange={(selected) => setContratoSelecionado(selected)}
                      placeholder="Todos"
                      isClearable
                      classNamePrefix="react-select"
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">ID da Autorização</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex: 123"
                      value={idBusca}
                      onChange={(e) => setIdBusca(e.target.value)}
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <label className="form-label">Código da Autorização</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Buscar código..."
                      value={codigoBusca}
                      onChange={(e) => setCodigoBusca(e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-sm-6 d-flex align-items-end gap-2">
                    <button
                      className="btn btn-primary"
                      onClick={aplicarFiltros}
                    >
                      <i className="ti ti-filter me-2" />
                      Filtrar
                    </button>
                    <button
                      className="btn btn-outline-secondary"
                      onClick={limparFiltros}
                    >
                      <i className="ti ti-x me-2" />
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Abas */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'todas' ? ' active' : ''}`}
                  onClick={() => { setActiveTab('todas'); setCurrentPage(1); }}
                >
                  Todas as autorizações
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link${activeTab === 'minhas' ? ' active' : ''}`}
                  onClick={() => { handleTabMinhas(); setCurrentPage(1); }}
                >
                  Minhas autorizações
                  {minhasComprasCount > 0 && (
                    <span className="badge ms-2" style={{ background: '#fd7e14', color: '#fff' }}>
                      {minhasComprasCount}
                    </span>
                  )}
                </button>
              </li>
              {podeAprovarPendente && (
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === 'pendentes' ? ' active' : ''}`}
                    onClick={() => { handleTabPendentes(); setCurrentPage(1); }}
                  >
                    Pendentes de Aprovação
                    {pendentesCount > 0 && (
                      <span className="badge ms-2" style={{ background: '#dc3545', color: '#fff' }}>
                        {pendentesCount}
                      </span>
                    )}
                  </button>
                </li>
              )}
            </ul>

            {/* Tabela */}
            <div className="card">
              <div className="card-body">
                {(isLoading || isLoadingMinhas || isLoadingPendentes) ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                ) : activeList.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ti ti-file-off" style={{ fontSize: '48px', color: '#ccc' }} />
                    <p className="text-muted mt-2">Nenhuma autorização encontrada</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Código</th>
                            <th
                              onClick={toggleSortData}
                              style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                            >
                              Data{' '}
                              <i className={`ti ${sortDirection === 'asc' ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ fontSize: '12px' }} />
                            </th>
                            <th>Contrato</th>
                            <th>Valor Total</th>
                            <th>Etapa</th>
                            <th>Cond. Pgto</th>
                            <th>NF Venda</th>
                            <th>Finalizado</th>
                            <th className="text-end">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((empenho) => (
                            <tr key={empenho.id}>
                              <td>{empenho.id}</td>
                              <td>
                                <button
                                  onClick={() => abrirDetalhes(empenho.id)}
                                  className="btn btn-link text-primary fw-bold p-0"
                                  style={{ textDecoration: 'none' }}
                                >
                                  {empenho.codigo_empenho || '-'}
                                </button>
                                {(empenho as any).id_empenho_pai && (
                                  <div>
                                    <small className="text-muted">
                                      <i className="ti ti-link me-1" style={{ fontSize: '10px' }}></i>
                                      Gerado a partir de #{(empenho as any).id_empenho_pai}
                                    </small>
                                  </div>
                                )}
                              </td>
                              <td>{formatarData(empenho.data_empenho)}</td>
                              <td>
                                <Link
                                  to={`/crm/contratos/detalhes/${empenho.id_contrato}`}
                                  className="text-truncate d-inline-block"
                                  style={{ maxWidth: '200px' }}
                                  title={`Contrato #${empenho.id_contrato}${(empenho as any).contrato_descricao ? ` - ${(empenho as any).contrato_descricao}` : ''}`}
                                >
                                  {(empenho as any).contrato_descricao
                                    ? (empenho as any).contrato_descricao
                                    : `Contrato #${empenho.id_contrato}`}
                                </Link>
                              </td>
                              <td><ProtectedValue module="AUTORIZACOES" value={formatarValor(empenho.valor_total)} /></td>
                              <td>
                                <span className={getEtapaBadgeClass(empenho.etapa)}>
                                  {getEtapaLabel(empenho.etapa)}
                                </span>
                              </td>
                              <td>
                                {empenho.condicao_pagamento === 'A_VISTA' ? 'À Vista' :
                                  empenho.condicao_pagamento === 'A_PRAZO' ? 'A Prazo' : '-'}
                              </td>
                              <td>
                                {empenho.numero_nf_venda ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ maxWidth: '120px' }} className="text-truncate" title={empenho.numero_nf_venda}>{empenho.numero_nf_venda.includes(',') ? 'Múltiplas NFs' : empenho.numero_nf_venda}</span>
                                    <button
                                      className="btn btn-sm btn-icon btn-outline-primary"
                                      onClick={() => abrirDetalhes(empenho.id, 'anexos')}
                                      title="Visualizar NFs"
                                    >
                                      <i className="ti ti-eye"></i>
                                    </button>
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                {(empenho as any).nome_romaneio ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <span style={{ maxWidth: '120px' }} className="text-truncate" title={(empenho as any).nome_romaneio}>{(empenho as any).nome_romaneio}</span>
                                    <button
                                      className="btn btn-sm btn-icon btn-outline-primary"
                                      onClick={() => abrirDetalhes(empenho.id, 'anexos')}
                                      title="Visualizar Romaneio"
                                    >
                                      <i className="ti ti-eye"></i>
                                    </button>
                                  </div>
                                ) : '-'}
                              </td>
                              <td className="text-end">
                                <div className="d-flex gap-1 justify-content-end">
                                  <button
                                    className="btn btn-sm btn-icon btn-outline-info"
                                    onClick={() => abrirDetalhes(empenho.id)}
                                    title="Ver Detalhes"
                                  >
                                    <i className="ti ti-eye" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, activeList.length)} de {activeList.length} autorizações
                        </div>
                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Anterior
                              </button>
                            </li>
                            {[...Array(totalPages)].map((_, index) => (
                              <li
                                key={index}
                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => paginate(index + 1)}
                                >
                                  {index + 1}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button
                                className="page-link"
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                Próximo
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas de Detalhes */}
      <EmpenhoDetalhes
        empenhoId={empenhoSelecionadoId}
        isOpen={isDetalhesOpen}
        onClose={fecharDetalhes}
        onUpdate={handleUpdateEmpenho}
        initialTab={initialModalTab}
      />

      {/* Backdrop para offcanvas */}
      {isDetalhesOpen && (
        <div
          className="offcanvas-backdrop fade show"
          onClick={fecharDetalhes}
        />
      )}
    </div>
  );
};

export default Empenhos;
